import { Big } from 'big.js';
import { match } from 'ts-pattern';
import { DisplayData, TenantJobLine } from '~/lib/model';
import { FIRST_VERSION_TIMESTAMP, ZodVersionedMetadata } from '~/lib/zod';
import BreJob from '~/tenants/bre/model/BreJob';
import { BreOrderType } from '~/tenants/bre/model/enums';
import BreGuidesConfig, { GuideType } from '~/tenants/bre/performable/print/guides/BreGuidesConfig';
import { BreGuidesRate } from '~/tenants/bre/performable/print/guides/BreGuidesData';
import { BreProviderId } from '~/tenants/bre/model/BreProvider';
import { datetime } from '~/lib/datettime';

export default class BreGuidesJob extends BreJob<typeof BreGuidesConfig> {
  get performable(): typeof BreGuidesConfig {
    return BreGuidesConfig;
  }

  get configurable(): boolean {
    return true;
  }

  get eligible(): boolean {
    return this.isOneOf(BreOrderType.AGENT_MKTG);
  }

  get assignee(): string | null {
    return BreProviderId.PRINT_TEAM.toString();
  }

  info(): DisplayData[] {
    const info = super.info();

    info.push({
      name: 'Types',
      value: this.metadata.types.map(BreGuidesJob.typeText),
      provider: true,
    });

    info.push({
      name: 'Need By',
      value: datetime(this.metadata.need_by, this.order.location.timezone).toFormat('LLL dd, yyyy'),
      provider: true,
    });

    return info;
  }

  static typeText(type: GuideType): string {
    return match(type)
      .with('selling', () => 'Selling')
      .with('buyers', () => 'Buyers')
      .with('after_sale', () => 'After the Sale')
      .with('preparing_for_sale', () => 'Preparing Your Home for Sale')
      .with('contract_to_close', () => 'Contract to Close')
      .exhaustive();
  }

  defaultValue(): ZodVersionedMetadata<(typeof BreGuidesConfig)['schema']> {
    return {
      version: FIRST_VERSION_TIMESTAMP,
      types: [],
      need_by: datetime(this.order.created, this.order.timezone).toSQLDate(),
    };
  }

  revenueLines(): TenantJobLine[] {
    const { types } = this.metadata;
    const { PDF } = BreGuidesRate;
    const lines: TenantJobLine[] = [];

    if (types.length > 0) {
      lines.push({
        id: 'pdf_guides',
        description: `PDF Guides (x${types.length})`,
        amount: new Big(PDF).times(types.length),
      });
    }

    return lines;
  }

  expenseLines(): TenantJobLine[] {
    const { types } = this.metadata;
    const { PDF } = BreGuidesRate;
    const lines: TenantJobLine[] = [];

    if (types.length > 0) {
      lines.push({
        id: 'pdf_guides',
        description: `PDF Guides (x${types.length})`,
        amount: new Big(PDF).times(types.length).times('0.85'),
      });
    }

    return lines;
  }
}
